import { ChevronRight, HomeIcon, ReceiptTextIcon, MapPin } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useLocation, Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { useSession } from "@/services/auth0/session";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Invoice } from "@/api.types";

interface NavItem {
  title: string;
  url: string;
  icon?: any;
  badge?: {
    text: string;
    color?: string;
    textColor?: string;
  };
  defaultOpen?: boolean;
  items?: {
    title: string;
    url: string;
    badge?: {
      text: string;
      color?: string;
      textColor?: string;
    };
  }[];
  hasArrow?: boolean;
}

export function NavMain() {
  const { pathname } = useLocation();
  const { customer, token } = useSession();

  const { data: invoicesData } = useQuery({
    queryKey: ["invoices", customer?.id],
    queryFn: async () => {
      const response = await axios.get<{ data: Invoice[] }>(
        `${import.meta.env.VITE_API_URL}/api/v1/customers/${customer?.id}/invoices`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    enabled: !!customer?.id,
  });

  // const openInvoicesCount = 12;
  const openInvoicesCount =
    invoicesData?.data?.filter(
      (invoice) => invoice.stripeInvoiceData?.status.toUpperCase() === "OPEN"
    ).length ?? 0;

  const navMain: NavItem[] = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: HomeIcon,
    },
    // {
    //   title: "Recipients Book",
    //   url: "/recipients",
    //   icon: NotebookTabsIcon,
    //   badge: { text: "Beta", color: "#adfa1d" },
    // },
    // {
    //   title: "Orders",
    //   url: "/orders",
    //   icon: Package2Icon,
    //   badge: { text: "2", color: "blue" },
    //   items: [
    //     { title: "Sent Orders", url: "/orders/sent" },
    //     {
    //       title: "Received Orders",
    //       url: "/orders/received",
    //       badge: { text: "New", color: "#adfa1d" },
    //     },
    //   ],
    //   hasArrow: true,
    // },
    {
      title: "Invoices",
      url: "/invoices",
      icon: ReceiptTextIcon,
      ...(openInvoicesCount > 0 && {
        badge: {
          text: openInvoicesCount.toString(),
          color: "blue",
          textColor: "white",
        },
      }),
    },
    {
      title: "My Address Book",
      url: "/my-address-book",
      icon: MapPin,
    },
    // {
    //   title: "Settings",
    //   url: "/settings",
    //   icon: Settings2,
    //   defaultOpen: true,
    //   items: [
    //     { title: "Profile", url: "/settings/profile" },
    //     ...(customer?.type === "BUSINESS"
    //       ? [{ title: "Business Account", url: "/settings/business-account" }]
    //       : []),
    //     { title: "My Address Book", url: "/settings/my-address-book" },
    //   ],
    //   hasArrow: true,
    // },
  ];

  return (
    <SidebarGroup>
      <SidebarMenu className="space-y-1">
        {navMain.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            className="group/collapsible"
            defaultOpen={item.defaultOpen}
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                {item.items ? (
                  <SidebarMenuButton
                    className="py-6 data-[active=true]:bg-white"
                    data-active={
                      pathname === item.url ||
                      item.items?.some((subItem) => pathname === subItem.url)
                    }
                  >
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <Badge
                        variant="default"
                        style={{
                          backgroundColor: item.badge.color ?? "#adfa1d",
                          color:
                            item.badge.textColor ??
                            (item.badge.color === "blue" ? "white" : "black"),
                        }}
                        className="hover:opacity-90 mr-2"
                      >
                        {item.badge.text}
                      </Badge>
                    )}
                    <ChevronRight className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                ) : (
                  <SidebarMenuButton
                    asChild
                    className="py-6 data-[active=true]:bg-white"
                    data-active={pathname === item.url}
                  >
                    <Link to={item.url}>
                      {item.icon && <item.icon className="h-4 w-4" />}
                      <span className="flex-1">{item.title}</span>
                      {item.badge && (
                        <Badge
                          variant="default"
                          style={{
                            backgroundColor: item.badge.color ?? "#adfa1d",
                            color:
                              item.badge.textColor ??
                              (item.badge.color === "blue" ? "white" : "black"),
                          }}
                          className="hover:opacity-90"
                        >
                          {item.badge.text}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                )}
              </CollapsibleTrigger>
              {item.items && (
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton
                          asChild
                          //   data-active={pathname === subItem.url}
                        >
                          <Link to={subItem.url} className="pl-8">
                            {subItem.title}
                            {subItem.badge && (
                              <Badge
                                variant="default"
                                style={{
                                  backgroundColor:
                                    subItem.badge.color ?? "#adfa1d",
                                  color:
                                    subItem.badge.textColor ??
                                    (subItem.badge.color === "blue"
                                      ? "white"
                                      : "black"),
                                }}
                                className="ml-2 hover:opacity-90"
                              >
                                {subItem.badge.text}
                              </Badge>
                            )}
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              )}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
